import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Video, VideoOff, Mic, MicOff, PhoneOff, Music, Users,
  AlertCircle, Loader2, Copy, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const HostVideoRoom = () => {
  const [searchParams] = useSearchParams();
  const sessionCode = searchParams.get('session');
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inSession, setInSession] = useState(false);
  const [clientConnected, setClientConnected] = useState(false);
  const [micEnabled, setMicEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  
  // WebRTC refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const wsRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);

  useEffect(() => {
    if (!sessionCode) {
      setError('No session code provided');
      setLoading(false);
      return;
    }
    fetchSessionAndJoin();
    
    return () => {
      // Cleanup on unmount
      cleanup();
    };
  }, [sessionCode]);

  const cleanup = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
    if (wsRef.current) {
      wsRef.current.close();
    }
  };

  const fetchSessionAndJoin = async () => {
    try {
      const res = await fetch(`${API}/api/live-studio/admin/sessions/${sessionCode}`);
      if (!res.ok) {
        setError('Session not found');
        return;
      }
      const data = await res.json();
      setSession(data.session);
      
      // Auto-join the video room
      await initializeVideoRoom(data.session.room_id || sessionCode);
    } catch (err) {
      setError('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const initializeVideoRoom = async (roomId) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      localStreamRef.current = stream;
      
      // Initialize WebRTC
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' }
        ]
      });
      peerConnectionRef.current = pc;
      
      // Add local tracks
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });
      
      // Handle remote tracks
      pc.ontrack = (event) => {
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = event.streams[0];
        }
        setClientConnected(true);
        toast.success('Client connected!');
      };
      
      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'ice-candidate',
            candidate: event.candidate
          }));
        }
      };
      
      // Connect to signaling server as HOST
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = API.replace('https://', '').replace('http://', '');
      console.log('Host connecting to room:', roomId);
      const ws = new WebSocket(`${wsProtocol}//${wsHost}/api/live-studio/ws/${roomId}/host`);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('Host WebSocket connected');
        setInSession(true);
        toast.success('Connected! Waiting for client...');
      };
      
      ws.onmessage = async (event) => {
        const message = JSON.parse(event.data);
        console.log('Host received:', message.type);
        
        if (message.type === 'peer_joined') {
          toast.info('Client is joining...');
          // HOST creates offer when client joins
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          ws.send(JSON.stringify({
            type: 'offer',
            sdp: offer.sdp
          }));
          console.log('Host sent offer');
        }
        
        // HOST receives answer from client
        if (message.type === 'answer') {
          console.log('Host received answer');
          await pc.setRemoteDescription(new RTCSessionDescription({
            type: 'answer',
            sdp: message.sdp
          }));
          console.log('Host set remote description');
        }
        
        if (message.type === 'ice-candidate' && message.candidate) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(message.candidate));
          } catch (e) {
            console.error('Error adding ICE candidate:', e);
          }
        }
        
        if (message.type === 'peer_left') {
          toast.info('Client disconnected');
          setClientConnected(false);
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = null;
          }
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        toast.error('Connection error');
      };
      
      ws.onclose = () => {
        console.log('WebSocket closed');
      };
      
    } catch (err) {
      console.error('Error initializing video:', err);
      toast.error('Could not access camera/microphone');
    }
  };

  const toggleMic = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setMicEnabled(track.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getVideoTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setVideoEnabled(track.enabled);
      }
    }
  };

  const endSession = () => {
    cleanup();
    window.location.href = '/admin/live-studio/sessions';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="bg-[#141414] rounded-2xl p-8 text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Error</h2>
          <p className="text-gray-400">{error}</p>
          <a 
            href="/admin/live-studio/sessions"
            className="mt-4 inline-block px-6 py-2 bg-green-500 text-black font-bold rounded-lg"
          >
            Back to Sessions
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{session?.title || 'Live Session'}</h1>
            <p className="text-gray-400 text-sm">Session Code: {sessionCode}</p>
          </div>
          <div className="flex items-center gap-2">
            {inSession ? (
              <span className="flex items-center gap-2 text-green-400">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                {clientConnected ? 'Client Connected' : 'Waiting for Client'}
              </span>
            ) : (
              <span className="text-gray-400">Connecting...</span>
            )}
          </div>
        </div>

        {/* SHARE THIS ROOM banner — always visible so admin can invite anyone at any time */}
        {sessionCode && (
          <div
            className="mb-4 p-4 rounded-2xl border-2 border-green-500/50 bg-gradient-to-r from-green-500/10 to-emerald-500/10"
            data-testid="host-share-banner"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Users className="w-6 h-6 text-green-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-green-100 font-medium">Share this session with your guest</p>
                  <p className="text-xs text-green-100/70 truncate">
                    Guest link: <span className="font-mono">{window.location.origin}/join/{sessionCode}</span>
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <button
                  type="button"
                  data-testid="host-copy-code-btn"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(sessionCode);
                      toast.success(`Code ${sessionCode} copied`);
                    } catch {
                      toast.info(sessionCode);
                    }
                  }}
                  className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-sm flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" /> Copy Code
                </button>
                <button
                  type="button"
                  data-testid="host-copy-link-btn"
                  onClick={async () => {
                    const link = `${window.location.origin}/join/${sessionCode}`;
                    try {
                      await navigator.clipboard.writeText(link);
                      toast.success('Guest link copied');
                    } catch {
                      toast.info(link);
                    }
                  }}
                  className="px-3 py-2 rounded-lg bg-green-500 hover:bg-green-400 text-black font-bold text-sm flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" /> Copy Guest Link
                </button>
                <a
                  href={`/join/${sessionCode}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="host-open-guest-view-btn"
                  className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" /> Open Guest View
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Video Area */}
        <div className="relative aspect-video bg-black rounded-2xl overflow-hidden">
          {/* Remote Video (Client) - Main */}
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
          
          {/* Placeholder when no client */}
          {!clientConnected && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 to-black">
              <div className="w-32 h-32 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center mb-4">
                <Users className="w-16 h-16 text-white" />
              </div>
              <h3 className="text-xl font-bold">Waiting for Client</h3>
              <p className="text-gray-400 mt-2">Share the join link with your client</p>
              <p className="text-green-400 text-sm mt-4 font-mono">
                {window.location.origin}/join/{sessionCode}
              </p>
            </div>
          )}
          
          {/* Local Video (Host) - Picture in Picture */}
          <div className="absolute bottom-4 right-4 w-48 aspect-video rounded-xl overflow-hidden border-2 border-green-500 bg-gray-900">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Live indicator */}
          <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2 flex items-center gap-3">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium">HOST</span>
          </div>
        </div>
        
        {/* Controls */}
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={toggleMic}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
              micEnabled ? 'bg-gray-800 hover:bg-gray-700' : 'bg-red-500 hover:bg-red-600'
            }`}
          >
            {micEnabled ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
          </button>
          
          <button
            onClick={toggleVideo}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
              videoEnabled ? 'bg-gray-800 hover:bg-gray-700' : 'bg-red-500 hover:bg-red-600'
            }`}
          >
            {videoEnabled ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
          </button>
          
          <button
            onClick={endSession}
            className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center"
          >
            <PhoneOff className="w-6 h-6" />
          </button>
        </div>
        
        {/* Client Info */}
        {session?.submission && (
          <div className="mt-6 bg-[#141414] rounded-xl p-4">
            <h3 className="font-bold mb-2">Client Info</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Name:</span>
                <p>{session.submission.name}</p>
              </div>
              <div>
                <span className="text-gray-400">Expects:</span>
                <p>{session.submission.expectations}</p>
              </div>
              <div>
                <span className="text-gray-400">Tracks:</span>
                <p>{session.submission.trackCount}</p>
              </div>
              <div>
                <span className="text-gray-400">Duration:</span>
                <p>{session.submission.duration} hour(s)</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HostVideoRoom;
